import { ArrowLeftOutlined, SettingOutlined } from '@ant-design/icons';
import {
  Badge,
  Button,
  Collapse,
  ConfigProvider,
  message,
  Popconfirm,
  Segmented,
  Skeleton,
  Space,
  Switch,
  theme,
} from 'antd';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { useGetNotifications } from '../../../utils/api/notifications/useGetNotifications';
import { useGetPreferences } from '../../../utils/api/notifications/useGetPreferences';
import { useMarkAllRead } from '../../../utils/api/notifications/useMarkAllRead';
import { useMarkRead } from '../../../utils/api/notifications/useMarkRead';
import { useResetPreferences } from '../../../utils/api/notifications/useResetPreferences';
import { useUpdatePreference } from '../../../utils/api/notifications/useUpdatePreference';
import {
  getDateLabel,
  getNotificationIcon,
  hasNotificationError,
} from '../../../utils/const/notifications';
import {
  StyledActions,
  StyledDrawer,
  StyledFooter,
  StyledList,
  StyledListItem,
  StyledTitle,
  StyledUnreadItem,
} from './styled';

const SCOPE_OPTIONS = [
  { label: 'Globales', value: 'global' },
  { label: 'Personnelles', value: 'personal' },
];

const NotificationDrawer = ({ open, setOpen }) => {
  const [scope, setScope] = useState('personal');
  const [view, setView] = useState('list');
  const navigate = useNavigate();
  const {
    token: { colorPrimary, colorFillSecondary, colorError, colorTextTertiary },
  } = theme.useToken();

  const { notifications, isLoading } = useGetNotifications(
    { scope, limit: 20 },
    { enabled: open }
  );

  const items = notifications ?? [];

  const { categories, isLoading: isLoadingPreferences } = useGetPreferences({
    enabled: open && view === 'preferences',
  });

  const [draft, setDraft] = useState({});

  const channelValue = (item, field) =>
    draft[item.code]?.[field] ?? item.preferences[field];

  const toggleChannel = (code, field, value) =>
    setDraft((current) => ({
      ...current,
      [code]: { ...current[code], [field]: value },
    }));

  const emailTypes = (category) =>
    category.items.filter((item) => item.available_channels.includes('email'));

  const categoryEmailValue = (category) => {
    const types = emailTypes(category);
    return (
      types.length > 0 &&
      types.every((item) => channelValue(item, 'email_enabled'))
    );
  };

  const toggleCategoryEmail = (category, value) =>
    setDraft((current) => {
      const next = { ...current };
      emailTypes(category).forEach((item) => {
        next[item.code] = { ...next[item.code], email_enabled: value };
      });
      return next;
    });

  const { updatePreference } = useUpdatePreference();
  const { mutate: resetPreferences } = useResetPreferences({
    onSuccess: () => setDraft({}),
    onError: (error) => {
      if (![401, 403].includes(error.response?.status)) {
        message.error("Les préférences n'ont pas pu être réinitialisées.");
      }
    },
  });

  const handleSave = async () => {
    const entries = Object.entries(draft);
    if (!entries.length) return;

    const results = await Promise.allSettled(
      entries.map(([code, body]) => updatePreference({ code, body }))
    );
    const failedDraft = Object.fromEntries(
      entries.filter((_, index) => results[index].status === 'rejected')
    );
    const failedCount = Object.keys(failedDraft).length;
    if (failedCount) {
      message.error(
        `${failedCount} préférence(s) n'ont pas pu être enregistrées.`
      );
    } else {
      message.success('Préférences enregistrées.');
    }
    setDraft(failedDraft);
  };

  const { mutate: markRead } = useMarkRead();
  const { mutate: markAllRead } = useMarkAllRead();

  const onClose = () => {
    setView('list');
    setOpen(false);
  };

  const handleItemClick = (item) => {
    if (!item.read_at) {
      markRead({ id: item.id });
    }
    setOpen(false);
    navigate('/dashboard/notifications');
  };

  const handleSeeAll = () => {
    setOpen(false);
    navigate('/dashboard/notifications');
  };

  const renderItem = (item, index) => {
    const Item = item.read_at ? StyledListItem : StyledUnreadItem;
    const Icon = getNotificationIcon(item.context);
    const isError = hasNotificationError(item.context);
    const dateLabel = getDateLabel(item.created_at);
    const previous = index > 0 ? items[index - 1] : null;
    const showDateLabel =
      !previous || getDateLabel(previous.created_at) !== dateLabel;
    return (
      <>
        {showDateLabel && (
          <li
            style={{
              listStyle: 'none',
              padding: '12px 12px 4px',
              fontSize: 12,
              color: colorTextTertiary,
            }}
          >
            {dateLabel}
          </li>
        )}
        <Item
          $background={colorFillSecondary}
          onClick={() => handleItemClick(item)}
        >
          <Badge
            dot={!item.read_at}
            styles={{ root: { flexShrink: 0, marginRight: 24 } }}
          >
            <Icon
              style={{
                fontSize: 12,
                color: isError ? colorError : colorPrimary,
                background: '#fff',
                padding: '10px',
                borderRadius: '8px',
              }}
            />
          </Badge>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 14,
                fontWeight: item.read_at ? 'normal' : 'bold',
              }}
            >
              {item.title}
            </div>
            <div style={{ fontSize: 12 }}>{item.message}</div>
            <div style={{ fontSize: 10 }}>
              {dayjs(item.created_at).format('HH:mm')}
            </div>
          </div>
        </Item>
      </>
    );
  };

  const renderPreference = (item) => (
    <div key={item.code} style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 13, marginBottom: 4 }}>{item.label}</div>
      <Space size="large">
        {item.available_channels.includes('in_app') && (
          <Space size="small">
            <Switch
              size="small"
              checked={channelValue(item, 'in_app_enabled')}
              disabled={item.locked_channels.includes('in_app')}
              onChange={(value) =>
                toggleChannel(item.code, 'in_app_enabled', value)
              }
            />
            <span style={{ fontSize: 12 }}>Dans l&apos;application</span>
          </Space>
        )}
        {item.available_channels.includes('email') && (
          <Space size="small">
            <Switch
              size="small"
              checked={channelValue(item, 'email_enabled')}
              disabled={item.locked_channels.includes('email')}
              onChange={(value) =>
                toggleChannel(item.code, 'email_enabled', value)
              }
            />
            <span style={{ fontSize: 12 }}>E-mail</span>
          </Space>
        )}
      </Space>
    </div>
  );

  const collapseItems = (categories ?? []).map((category) => ({
    key: category.name,
    label: <span style={{ fontWeight: 600 }}>{category.name}</span>,
    extra: (
      <Space size="small" onClick={(event) => event.stopPropagation()}>
        <Switch
          size="small"
          checked={categoryEmailValue(category)}
          onChange={(value) => toggleCategoryEmail(category, value)}
        />
        <span style={{ fontSize: 12 }}>E-mail</span>
      </Space>
    ),
    children: category.items.map(renderPreference),
  }));

  return (
    <StyledDrawer
      title={
        <StyledTitle level={4}>
          {view === 'list' ? 'Notifications' : 'Paramètres notifications'}
        </StyledTitle>
      }
      placement="right"
      closable={true}
      onClose={onClose}
      open={open}
      getContainer={false}
      extra={
        view === 'list' ? (
          <Button
            type="text"
            icon={<SettingOutlined />}
            onClick={() => setView('preferences')}
          />
        ) : null
      }
    >
      {view === 'list' ? (
        <>
          <ConfigProvider
            theme={{
              components: {
                Segmented: {
                  itemSelectedBg: colorPrimary,
                  itemSelectedColor: '#fff',
                },
              },
            }}
          >
            <Segmented
              block
              size="large"
              options={SCOPE_OPTIONS}
              value={scope}
              onChange={setScope}
              style={{ marginBottom: 8 }}
            />
          </ConfigProvider>
          <StyledActions>
            <Button type="link" onClick={() => markAllRead()}>
              Tout marquer comme lu
            </Button>
          </StyledActions>
          <StyledList
            loading={isLoading}
            dataSource={items}
            renderItem={renderItem}
          />
          <StyledFooter>
            <Button type="link" onClick={handleSeeAll}>
              Voir toutes mes notifications
            </Button>
          </StyledFooter>
        </>
      ) : (
        <>
          <Button
            type="link"
            icon={<ArrowLeftOutlined />}
            onClick={() => setView('list')}
            style={{ paddingLeft: 0, marginBottom: 12 }}
          >
            Retour aux notifications
          </Button>
          <div style={{ margin: '8px 0 16px' }}>
            Je souhaite recevoir les notifications pour :
          </div>
          {isLoadingPreferences ? (
            <Skeleton active />
          ) : (
            <ConfigProvider
              theme={{ components: { Collapse: { headerBg: 'transparent' } } }}
            >
              <Collapse bordered={false} items={collapseItems} />
            </ConfigProvider>
          )}
          <StyledActions
            style={{ paddingTop: 16, justifyContent: 'space-between' }}
          >
            <Popconfirm
              title="Réinitialiser toutes vos préférences ?"
              onConfirm={() => resetPreferences()}
              okText="Réinitialiser"
              cancelText="Annuler"
            >
              <Button>Réinitialiser</Button>
            </Popconfirm>
            <Button type="primary" onClick={handleSave}>
              Enregistrer
            </Button>
          </StyledActions>
        </>
      )}
    </StyledDrawer>
  );
};

export default NotificationDrawer;
